import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { REGISTER_MUTATION } from '../../apollo/queries';
import { useLoading } from '../../contexts/LoadingContext';
import '../css/SignUpPage.css';

const SignUpPageContent = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
    const { startLoading, stopLoading, updateMessage } = useLoading();
    
    // GraphQL 회원가입 뮤테이션
    const [registerUser] = useMutation(REGISTER_MUTATION, {
        onCompleted: (data) => {
            // 토큰을 로컬스토리지에 저장
            localStorage.setItem('authToken', data.register.token);
            // 사용자 정보도 저장 (옵션)
            localStorage.setItem('user', JSON.stringify(data.register.user));
            setLoading(false);
            stopLoading();
            setStep(3); // 완료 단계로 이동
        },
        onError: (error) => {
            setLoading(false);
            stopLoading();
            console.error('회원가입 에러:', error);
            
            // GraphQL 에러 메시지 처리
            let errorMessage = '회원가입 중 오류가 발생했습니다.';
            
            if (error.graphQLErrors?.length > 0) {
                errorMessage = error.graphQLErrors[0].message;
                if (errorMessage.includes('이메일')) {
                    setErrors({ email: errorMessage });
                } else if (errorMessage.includes('비밀번호')) {
                    setErrors({ password: errorMessage });
                } else if (errorMessage.includes('이름')) {
                    setErrors({ name: errorMessage });
                } else {
                    setErrors({ general: errorMessage });
                }
            } else if (error.networkError) {
                errorMessage = '네트워크 오류가 발생했습니다. 다시 시도해주세요.';
                setErrors({ general: errorMessage });
            } else if (error.message) {
                errorMessage = error.message;
                setErrors({ general: errorMessage });
            } else {
                setErrors({ general: errorMessage });
            }
            
            // Alert로 에러 메시지 표시
            alert(errorMessage);
        }
    });
    
    const [formData, setFormData] = useState({
        // 회사 정보
        companyName: '',
        ceoName: '',
        zipCode: '',
        address: '',
        addressDetail: '',
        businessNumber: '',
        
        // 로그인 정보
        email: '',
        password: '',
        passwordConfirm: '',
        
        // 사용자 정보
        managerName: '',
        department: '',
        phone: '',
        mobile: '',
        fax: '',
        charge: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // 에러 메시지 클리어
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateStep1 = () => {
        const newErrors = {};
        
        // 필수 필드 검증
        if (!formData.companyName.trim()) {
            newErrors.companyName = '회사명을 입력해주세요.';
        }
        
        if (!formData.ceoName.trim()) {
            newErrors.ceoName = '대표자명을 입력해주세요.';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = '이메일을 입력해주세요.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = '올바른 이메일 형식을 입력해주세요.';
        }
        
        if (!formData.password.trim()) {
            newErrors.password = '비밀번호를 입력해주세요.';
        } else if (formData.password.length < 6) {
            newErrors.password = '비밀번호는 최소 6자 이상이어야 합니다.';
        }
        
        if (formData.password !== formData.passwordConfirm) {
            newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
        }
        
        if (!formData.managerName.trim()) {
            newErrors.managerName = '담당자명을 입력해주세요.';
        }
        
        if (!formData.phone.trim()) {
            newErrors.phone = '전화번호를 입력해주세요.';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (step === 1) {
            if (validateStep1()) {
                setStep(2);
            }
        } else if (step === 2) {
            handleSubmit();
        }
    };

    const handleBack = () => setStep(prev => prev - 1);

    const handleSubmit = async () => {
        setLoading(true);
        setErrors({});
        startLoading('회원가입 처리 중...');
        
        try {
            await registerUser({
                variables: {
                    input: {
                        // 로그인 정보
                        email: formData.email,
                        password: formData.password,
                        name: formData.managerName,
                        phone: formData.phone,
                        
                        // 회사 정보
                        company: formData.companyName,
                        ceoName: formData.ceoName,
                        zipCode: formData.zipCode,
                        address: formData.address,
                        addressDetail: formData.addressDetail,
                        businessNumber: formData.businessNumber || null,
                        
                        // 사용자 상세 정보
                        department: formData.department,
                        mobile: formData.mobile,
                        fax: formData.fax
                    }
                }
            });
        } catch (error) {
            // 에러는 onError에서 처리됨
            console.error('회원가입 실행 에러:', error);
        }
    };

    const renderProgressBar = () => (
        <div className="co-progress-indicator">
            <div className="co-progress-line-bg"></div>
            <div className={`co-progress-line-fill ${step >= 1 ? 'step1' : ''} ${step >= 2 ? 'step2' : ''} ${step >= 3 ? 'step3' : ''}`}></div>
            <div className="step-item active">
                <div className="step-dot active"></div>
                <div className="step-label">정보입력</div>
            </div>
            <div className={`step-item ${step >= 2 ? 'active' : ''}`}>
                <div className={`step-dot ${step >= 2 ? 'active' : ''}`}></div>
                <div className="step-label">입력 내용 확인</div>
            </div>
            <div className={`step-item ${step >= 3 ? 'active' : ''}`}>
                <div className={`step-dot ${step >= 3 ? 'active' : ''}`}></div>
                <div className="step-label">접수완료</div>
            </div>
        </div>
    );

    const renderError = (fieldName) => {
        if (errors[fieldName]) {
            return <div className="sup-error-message">{errors[fieldName]}</div>;
        }
        return null;
    };

    // Daum 주소검색 초기화
    useEffect(() => {
        if (isAddressSearchOpen && window.daum && window.daum.Postcode) {
            const element_layer = document.getElementById('address_search_layer');
            
            new window.daum.Postcode({
                oncomplete: function(data) {
                    // 팝업에서 검색결과 항목을 클릭했을때 실행할 코드를 작성하는 부분.
                    let addr = ''; // 주소 변수
                    let extraAddr = ''; // 참고항목 변수

                    // 사용자가 선택한 주소 타입에 따라 해당 주소 값을 가져온다.
                    if (data.userSelectedType === 'R') { // 사용자가 도로명 주소를 선택했을 경우
                        addr = data.roadAddress;
                    } else { // 사용자가 지번 주소를 선택했을 경우(J)
                        addr = data.jibunAddress;
                    }

                    // 사용자가 선택한 주소가 도로명 타입일때 참고항목을 조합한다.
                    if(data.userSelectedType === 'R'){
                        // 법정동명이 있을 경우 추가한다. (법정리는 제외)
                        // 법정동의 경우 마지막 문자가 "동/로/가"로 끝난다.
                        if(data.bname !== '' && /[동|로|가]$/g.test(data.bname)){
                            extraAddr += data.bname;
                        }
                        // 건물명이 있고, 공동주택일 경우 추가한다.
                        if(data.buildingName !== '' && data.apartment === 'Y'){
                            extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName);
                        }
                        // 표시할 참고항목이 있을 경우, 괄호까지 추가한 최종 문자열을 만든다.
                        if(extraAddr !== ''){
                            extraAddr = ' (' + extraAddr + ')';
                        }
                    }

                    setFormData(prev => ({
                        ...prev,
                        zipCode: data.zonecode,
                        address: addr + extraAddr
                    }));

                    // 주소 선택 후 주소검색 레이어 닫기
                    setIsAddressSearchOpen(false);
                },
                onresize: function(size) {
                    element_layer.style.height = size.height + 'px';
                },
                width: '100%',
                height: '100%'
            }).embed(element_layer);

            // 주소검색 레이어 표시
            element_layer.style.display = 'block';
        }
    }, [isAddressSearchOpen]);

    const handleAddressSearch = () => {
        if (!window.daum || !window.daum.Postcode) {
            alert('주소 검색 서비스를 불러올 수 없습니다.');
            return;
        }
        setIsAddressSearchOpen(true);
    };

    const closeAddressSearch = () => {
        setIsAddressSearchOpen(false);
        const element_layer = document.getElementById('address_search_layer');
        if (element_layer) {
            element_layer.style.display = 'none';
        }
    };

    const renderStep1 = () => (
        <>
            {renderProgressBar()}
            
            {errors.general && (
                <div className="sup-error-banner">
                    {errors.general}
                </div>
            )}
            
            <div className="sup-form-section">
                <h2 className="sup-section-title">회사 정보</h2>
                <div className="sup-form-grid">
                    <div className="sup-form-row">
                        <label>회사명 <span className="req">*</span></label>
                        <input 
                            type="text" 
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            placeholder="예: 부스바월드" 
                        />
                        {renderError('companyName')}
                    </div>
                    <div className="sup-form-row">
                        <label>대표자명 <span className="req">*</span></label>
                        <input 
                            type="text" 
                            name="ceoName"
                            value={formData.ceoName}
                            onChange={handleInputChange}
                            placeholder="예: 홍길동" 
                        />
                        {renderError('ceoName')}
                    </div>
                    <div className="sup-form-row">
                        <label>사업자 등록번호</label>
                        <input 
                            type="text" 
                            name="businessNumber"
                            value={formData.businessNumber}
                            onChange={handleInputChange}
                            placeholder="예: 123-45-67890 (하이픈 없이 입력 가능)" 
                        />
                    </div>
                    <div className="sup-form-row sup-zip-row">
                        <label>우편번호</label>
                        <div className="sup-form-field">
                            <input 
                                type="text" 
                                name="zipCode"
                                value={formData.zipCode}
                                onChange={handleInputChange}
                                placeholder="우편번호" 
                            />
                            <button type="button" className="sup-btn-inline" onClick={handleAddressSearch}>주소 검색</button>
                        </div>
                    </div>
                    <div className="sup-form-row">
                        <label>기본 주소</label>
                        <input 
                            type="text" 
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            placeholder="주소 검색후 자동 입력됩니다." 
                        />
                    </div>
                    <div className="sup-form-row">
                        <label>상세 주소</label>
                        <input 
                            type="text" 
                            name="addressDetail"
                            value={formData.addressDetail}
                            onChange={handleInputChange}
                            placeholder="상세 주소를 입력해 주세요." 
                        />
                    </div>
                </div>
            </div>

            <div className="sup-form-section">
                <h2 className="sup-section-title">로그인 정보</h2>
                <div className="sup-form-grid">
                    <div className="sup-form-row">
                        <label>이메일 주소 <span className="req">*</span></label>
                        <input 
                            type="email" 
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="이메일을 입력해주세요" 
                        />
                        {renderError('email')}
                    </div>
                    <div className="sup-form-row">
                        <label>비밀번호 <span className="req">*</span></label>
                        <input 
                            type="password" 
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="비밀번호를 입력해 주세요" 
                        />
                        {renderError('password')}
                        <div className="sup-field-note">
                            <ul>
                                <li>최소 6자 이상 입력해 주세요.</li>
                                <li>영문과 숫자를 모두 포함하는 것을 권장합니다.</li>
                            </ul>
                        </div>
                    </div>
                    <div className="sup-form-row">
                        <label>비밀번호 재확인 <span className="req">*</span></label>
                        <input 
                            type="password" 
                            name="passwordConfirm"
                            value={formData.passwordConfirm}
                            onChange={handleInputChange}
                            placeholder="확인을 위해 다시 입력해 주세요" 
                        />
                        {renderError('passwordConfirm')}
                    </div>
                </div>
            </div>
            
            <div className="sup-form-section">
                <h2 className="sup-section-title">사용자 정보</h2>
                <div className="sup-form-grid">
                    <div className="sup-form-row">
                        <label>담당자 <span className="req">*</span></label>
                        <input 
                            type="text" 
                            name="managerName"
                            value={formData.managerName}
                            onChange={handleInputChange}
                            placeholder="담당자명을 입력해주세요"
                        />
                        {renderError('managerName')}
                    </div>
                    <div className="sup-form-row">
                        <label>부서/역할</label>
                        <input 
                            type="text" 
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            placeholder="부서/역할을 입력해주세요"
                        />
                    </div>
                    <div className="sup-form-row">
                        <label>전화번호 <span className="req">*</span></label>
                        <input 
                            type="text" 
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="전화번호를 입력해주세요"
                        />
                        {renderError('phone')}
                    </div>
                    <div className="sup-form-row">
                        <label>휴대전화</label>
                        <input 
                            type="text" 
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            placeholder="휴대전화를 입력해주세요"
                        />
                    </div>
                    <div className="sup-form-row">
                        <label>FAX</label>
                        <input 
                            type="text" 
                            name="fax"
                            value={formData.fax}
                            onChange={handleInputChange}
                            placeholder="팩스번호를 입력해주세요"
                        />
                    </div>
                </div>
            </div>
            <div className="sup-actions">
                <button 
                    className="sup-btn-primary" 
                    onClick={handleNext}
                    disabled={loading}
                >
                    {loading ? '처리중...' : '입력 완료'}
                </button>
            </div>
        </>
    );

    const renderStep2 = () => (
        <>
            {renderProgressBar()}
            <div className="sup-form-section">
                <h2 className="sup-section-title">회사 정보</h2>
                <div className="sup-summary-grid">
                    <div className="sup-summary-row"><span className="label">회사명</span><span>{formData.companyName}</span></div>
                    <div className="sup-summary-row"><span className="label">대표자명</span><span>{formData.ceoName}</span></div>
                    {formData.businessNumber && <div className="sup-summary-row"><span className="label">사업자 등록번호</span><span>{formData.businessNumber}</span></div>}
                    {formData.zipCode && <div className="sup-summary-row"><span className="label">우편번호</span><span>{formData.zipCode}</span></div>}
                    {formData.address && <div className="sup-summary-row"><span className="label">기본 주소</span><span>{formData.address}</span></div>}
                    {formData.addressDetail && <div className="sup-summary-row"><span className="label">상세 주소</span><span>{formData.addressDetail}</span></div>}
                </div>
            </div>
            <div className="sup-form-section">
                <h2 className="sup-section-title">사용자 정보</h2>
                <div className="sup-summary-grid">
                    <div className="sup-summary-row"><span className="label">담당자</span><span>{formData.managerName}</span></div>
                    <div className="sup-summary-row"><span className="label">전화번호</span><span>{formData.phone}</span></div>
                    {formData.mobile && <div className="sup-summary-row"><span className="label">휴대전화</span><span>{formData.mobile}</span></div>}
                    {formData.fax && <div className="sup-summary-row"><span className="label">FAX</span><span>{formData.fax}</span></div>}
                    {formData.department && <div className="sup-summary-row"><span className="label">부서 / 역할</span><span>{formData.department}</span></div>}
                    <div className="sup-summary-row"><span className="label">이메일 주소</span><span>{formData.email}</span></div>
                </div>
            </div>
            
            {errors.general && (
                <div className="sup-error-banner">
                    {errors.general}
                </div>
            )}
            
            <div className="sup-actions space-between">
                <button className="sup-btn-dark" onClick={handleBack} disabled={loading}>돌아가기</button>
                <button 
                    className="sup-btn-primary" 
                    onClick={handleNext}
                    disabled={loading}
                >
                    {loading ? '가입 처리중...' : '회원가입 완료'}
                </button>
            </div>
        </>
    );

    const renderStep3 = () => (
        <>
            {renderProgressBar()}
            <div className="sup-final-container">
                <h2>회원 가입해주셔서 감사합니다.</h2>
                <p>등록이 완료되었습니다.</p>
                <p>로그인하여 서비스를 이용해보세요.</p>
                <div className="sup-final-actions">
                    <button className="sup-btn-primary" onClick={() => navigate('/')}>로그인 하기</button>
                    <button className="sup-btn-dark" onClick={() => navigate('/')}>홈으로 가기</button>
                </div>
            </div>
        </>
    );

    const renderStep = () => {
        switch (step) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            default: return renderStep1();
        }
    }

    return (
        <div className="signup-page">
            <div className="sup-breadcrumb">
                <span>홈</span> &gt; <span className="current">회원가입</span>
            </div>
            <h1 className="sup-main-title">회원가입</h1>
            {renderStep()}
            
            {/* 주소검색 레이어 */}
            {isAddressSearchOpen && (
                <div className="address-search-overlay">
                    <div className="address-search-modal">
                        <div className="address-search-header">
                            <h3>주소검색</h3>
                            <button className="address-search-close" onClick={closeAddressSearch}>×</button>
                        </div>
                        <div id="address_search_layer" style={{ width: '100%', height: '400px', display: 'none' }}></div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SignUpPage = () => (
    <SignUpPageContent />
);

export default SignUpPage; 